import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Onde a página começa depois de uma navegação.
 *
 * Ir para o topo é o certo para uma rota nova, mas não quando a URL traz uma
 * âncora: `/#precos` vindo de outra página fazia carga completa, o navegador
 * ia até a seção e este efeito puxava de volta para o topo logo depois — e
 * como ele roda num `useLayoutEffect`, ganhava sempre. Era por isso que todo
 * link de âncora entre páginas (os itens de nav do header fora da landing e os
 * cinco links de produto do rodapé) chegava no topo da landing e parava ali.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  useEffect(() => {
    if (!hash) return;

    // Um quadro depois do commit: numa carga completa o React ainda não pintou
    // a seção quando o navegador tenta resolver o fragmento sozinho, então a
    // rolagem nativa não tem alvo. Aqui tem.
    //
    // `scrollIntoView` e não a rolagem nativa também por causa do
    // `content-visibility: auto` das seções da landing: com o alvo abaixo
    // delas, o que existe é uma altura estimada, e é a posição calculada
    // agora — não a que o navegador tinha no parse — que acerta.
    const quadro = requestAnimationFrame(() => {
      document
        .getElementById(decodeURIComponent(hash.slice(1)))
        ?.scrollIntoView();
    });

    return () => cancelAnimationFrame(quadro);
  }, [pathname, hash]);

  return null;
}

export default ScrollToTop;
